import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { track } from '@/lib/analytics';
import {
  COMPETITION_VIDEOS, COMPETITION_VIDEO_EVENT, embeddableVideo, markCompetitionIntroSeen, useCompetition,
  type CompetitionVideoKind,
} from '@/lib/competition';

/****************
 * THE COMPETITION VIDEOS. 2026-10-26 (Mishka sent both).
 *
 *   long (1 min 40)  after the chooser's orange answer, "I'm here for the
 *                    competition"
 *   short (34 s)     the first time someone opens the competition page; it
 *                    then stays on the page to be watched again
 *
 * Both are files in public/videos, like the welcome videos. A link an admin
 * pastes on the competition page (video_url) replaces the long one, so a new
 * cut needs no deploy. The captions are burned into the picture.
 ****************/
export function CompetitionVideoDialog() {
  const { t } = useTranslation('competition');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [kind, setKind] = useState<CompetitionVideoKind | null>(null);
  const open = kind !== null;
  const { data: info } = useCompetition(open);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const k = (e as CustomEvent<{ kind?: CompetitionVideoKind }>).detail?.kind ?? 'long';
      setKind(k);
      // Whoever has seen a competition video is not shown the short one again by itself.
      markCompetitionIntroSeen();
      track('competition_opened', k === 'long' ? 'chooser' : 'intro');
    };
    window.addEventListener(COMPETITION_VIDEO_EVENT, onOpen);
    return () => window.removeEventListener(COMPETITION_VIDEO_EVENT, onOpen);
  }, []);

  // The bundled file plays until the settings say an admin pasted another link.
  const pasted = kind === 'long' && info ? embeddableVideo(info.video_url) : null;
  const file = kind ? COMPETITION_VIDEOS[kind] : null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) setKind(null); }}>
      <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 text-start">
          <DialogTitle>{t('video.title')}</DialogTitle>
          <DialogDescription>{t('video.description')}</DialogDescription>
        </DialogHeader>
        {open && pasted?.kind === 'frame' && (
          <iframe
            src={pasted.src}
            title={t('video.title')}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full aspect-video bg-black border-0"
          />
        )}
        {open && file && pasted?.kind !== 'frame' && (
          <video
            key={kind}
            src={pasted?.kind === 'file' ? pasted.src : file.src}
            poster={pasted ? undefined : file.poster}
            controls
            autoPlay
            playsInline
            preload="metadata"
            className="w-full aspect-video bg-black"
          />
        )}
        <div className="flex justify-end gap-2 px-6 py-4">
          <Button variant="outline" onClick={() => setKind(null)}>{t('video.later')}</Button>
          {pathname !== '/competition' && (
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => { setKind(null); navigate('/competition'); }}
            >
              {t('video.cta')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
